import arrayTools from "_src/engine/utility/arrays";
import renderGraph from "_src/render_graph/render_graph";
import { screenUtils } from "_src/client/utils";

const OBJECT_ID = 0;
const GENERAL_PURPOSE = 1;
const SECTION_ANALYSIS = 2;

class BufferObject {
  constructor() {
    this.data = null;
    this.width = 0;
    this.updatePromise = null;
  }
}

export const BufferReaderType = {
  OBJECT_ID,
  GENERAL_PURPOSE,
  SECTION_ANALYSIS,
}

export default class GPUBufferReader {

  constructor(graph) {
    this._buffers = Array(3);
    for (let i=0; i<this._buffers.length; i++) {
      this._buffers[i] = new BufferObject();
    }

    this._strIDtoIntID = new Map();
    this._intIDtoStrID = new Map();

    this._graph = graph;
  }

  dropBuffers() {
    for (let i=0; i<this._buffers.length; i++) {
      const buffer = this._buffers[i];

      if (buffer.data) {
        arrayTools.returnArrayBuffer(buffer.data.buffer);
      }

      this._buffers[i] = new BufferObject();
    }
  }

  getBufferData(bufferID) {
    return this._buffers[bufferID].data;
  }

  async readPixelGPU(textureX, textureY, bufferID) {
    const pixel = await this._readPixel(bufferID, textureX, textureY);

    if (!pixel || pixel[3] === 0) {
      return this.invalidResult(bufferID);
    }

    const r = pixel[0];
    const g = pixel[1];
    const b = pixel[2];

    arrayTools.returnArrayBuffer(pixel.buffer);

    return this.validResult(r, g, b, bufferID);
  }

  async readPixelCache(textureX, textureY, bufferID) {
    const buffer = await this._getUpdatedBuffer(bufferID);

    let pixelBase = Math.floor(textureY * Math.floor(buffer.width) + textureX) * 4;
    if (bufferID === SECTION_ANALYSIS) {
      const pixelBaseOpt = _pickInRadius(buffer.data, buffer.width, textureX, textureY, 10);
      if (pixelBaseOpt) pixelBase = pixelBaseOpt;
    }

    if (buffer.data[pixelBase + 3] === 0) {
      return this.invalidResult(bufferID);
    }

    const r = buffer.data[pixelBase];
    const g = buffer.data[pixelBase + 1];
    const b = buffer.data[pixelBase + 2];

    return this.validResult(r, g, b, bufferID);
  }

  validResult(r, g, b, bufferID) {
    switch (bufferID) {
      case OBJECT_ID:
        return this._intIDtoStrID.get(rgbToInt(r, g, b));
      case GENERAL_PURPOSE:
      case SECTION_ANALYSIS:
        return rgbToFloat(r, g, b);
    }
  }

  invalidResult(bufferID) {
    switch (bufferID) {
      case OBJECT_ID:
        return null;
      case GENERAL_PURPOSE:
      case SECTION_ANALYSIS:
        return -1;
    }
  }

  depthToWorldSpace(depth, clientX, clientY, windowViewport, camera, dest) {

    const {x, y} = screenUtils.toScreen(windowViewport, camera.viewport,
        clientX, clientY);

    return camera.unprojectPoint(x, y, 2 * depth - 1, dest);

  }

  intID(objectID) {

    if (!this._strIDtoIntID.has(objectID)) {

      this._strIDtoIntID.set(objectID, this._strIDtoIntID.size);
      this._intIDtoStrID.set(this._intIDtoStrID.size, objectID);

    }

    return this._strIDtoIntID.get(objectID);

  }

  cleanup() {
    this.dropBuffers();

    this._strIDtoIntID.clear();
    this._intIDtoStrID.clear();
  }

  async _getUpdatedBuffer(bufferID) {
    const buffer = this._buffers[bufferID];
    if (buffer.data === null) {
      if (!buffer.updatePromise) {
        buffer.updatePromise = this._updateBuffer(bufferID).then(() => {
          buffer.updatePromise = null;
        });
      }

      await buffer.updatePromise;
    }

    return buffer;
  }

  async _updateBuffer(bufferID) {
    const buffer = this._buffers[bufferID];

    const mainPass = this._graph.getPass(renderGraph.MAIN);
    const camera = mainPass.getMainScene().camera;

    switch (bufferID) {
      case OBJECT_ID:
        buffer.data = await this._graph.getObjectIDBuffer(camera.viewport);
        break;
      case GENERAL_PURPOSE:
        buffer.data = await this._graph.getGeneralPurposeBuffer(camera.viewport);
        break;
      case SECTION_ANALYSIS:
        buffer.data = await this._graph.getSectionAnalysisBuffer(camera.viewport);
        break;
    }

    buffer.width = camera.viewport[2];
  }

  _readPixel(bufferID, textureX, textureY) {
    const viewport = [textureX, textureY, 1, 1];
    switch (bufferID) {
      case OBJECT_ID:
        return this._graph.getObjectIDBuffer(viewport);
      case GENERAL_PURPOSE:
        return this._graph.getGeneralPurposeBuffer(viewport);
      case SECTION_ANALYSIS:
        return this._graph.getSectionAnalysisBuffer(viewport);
    }
  }

}

const rgbToFloat = (function() {
  const depthScale = [65536.0 / 65793.0 / 255.0, 256.0 / 65793.0 / 255.0, 1.0 / 65793.0 / 255.0];

  return function (r, g, b) {
    return r * depthScale[0] + g * depthScale[1] + b * depthScale[2];
  }
})();

function rgbToInt(r, g, b) {
  return r * 65536 + g * 256 + b;
}

function _pickInRadius(buffer, bufferWidth, x, y, radius) {
  let bestDistance = radius + 1;
  let bestCoord = null;
  for (let k=-radius; k<=radius; k++) {
    for (let m=-radius; m<=radius; m++) {
      const distance = Math.sqrt(k*k + m*m);
      if (distance > radius) continue;

      const xx = x + k;
      const yy = y + m;
      const ii = (yy * bufferWidth + xx) * 4;
      if (ii + 3 >= buffer.length || ii < 0) continue;

      const isValid = buffer[ii + 3] !== 0;
      if (isValid && distance < bestDistance) {
        bestDistance = distance;
        bestCoord = ii;
      }
    }
  }

  return bestCoord;
}
